import {Holiday, Person, TimeRegistrations} from '../TimesheetsCalendarTypes';
import {CalendarEventType} from '../calendar-events/CalendarEventsFetch';
import {useIntl} from 'react-intl';
import {isDaySelected, onKeyboardEventHandler, workingHourForTheDay} from '../TimesheetsCalendarUtil';
import {format, getWeek, isBefore, isSameDay, isToday} from 'date-fns';
import {CellText, CellTextBold, DayCellStyle, DayInfoWrapper} from '../TimesheetsCalendar_styled';
import {TodayText} from './DayCell_styled';
import {FlexColumn, FlexRow, Icon} from '@forecast-it/design-system';
import React, {useEffect, useRef, useState} from 'react';
import {getTimeRegistrationsOfTheDay, separateWorkingTimeAndTimeOff} from '../../timesheets_time_registrations';
import Util from '../../../../shared/util/util';
import {CalendarEvents} from '../calendar-events/CalendarEvents';
import {isEventConvertedType} from '../calendar-events/useConvertedEventTracking';

type DayCellProps = {
	timeRegistrations: TimeRegistrations;
	currentPersonViewed: Person;
	selectedDate: string;
	onSelect: () => void;
	lockedDate: Date;
	day: Date;
	isDayOutsideViewingMonth: boolean;
	holidaysOfTheDay: Holiday[];
	calendarEvents: CalendarEventType[];
	isEventConverted: isEventConvertedType;
	isLoadingCalendarEvents: boolean;
	currentViewingDate: Date;
	openModal: (calendarEvent: CalendarEventType) => void;
	showContextMenu: (mouseEvent: MouseEvent, calendarEvent: CalendarEventType) => void;
};

export const DayCell = ({
	timeRegistrations,
	currentPersonViewed,
	selectedDate,
	onSelect,
	lockedDate,
	day,
	isDayOutsideViewingMonth,
	holidaysOfTheDay,
	calendarEvents,
	isEventConverted,
	isLoadingCalendarEvents,
	currentViewingDate,
	openModal,
	showContextMenu,
}: DayCellProps): React.JSX.Element => {
	const intl = useIntl();
	const ref = useRef<HTMLDivElement>(null);
	const timeRegistrationsForDay = getTimeRegistrationsOfTheDay(day, timeRegistrations.edges);
	const workingHours = workingHourForTheDay(currentPersonViewed, day);
	const timeWorked = separateWorkingTimeAndTimeOff(timeRegistrationsForDay);
	const totalTime = timeWorked.timeOff + timeWorked.workingTime;
	const isDayFullTimeOff = timeWorked.timeOff > 0 && workingHours - timeWorked.timeOff <= 0;
	const totalTimeInHoursFormatted = Util.convertMinutesToFullHour(totalTime, intl, true);
	const workingHoursFormatted = Util.convertMinutesToFullHour(workingHours, intl, true);
	const daySelected = isDaySelected(selectedDate, day);
	const isHoliday = holidaysOfTheDay.length > 0;
	const isLocked = lockedDate && (isSameDay(day, lockedDate) || isBefore(day, lockedDate));
	const isDayToday = isToday(day);
	const checkMarkColor = isLocked ? 'disabledOnDisabled' : isDayOutsideViewingMonth ? 'disabled' : 'success';
	const isWeekSelected = getWeek(currentViewingDate) === getWeek(day);

	const showCheckMark = !isDayFullTimeOff && totalTime >= workingHours && workingHours > 0;
	const isNonWorkingDay = isHoliday || isDayFullTimeOff || workingHours <= 0;
	const showTotalTime = timeRegistrationsForDay.length > 0;
	const showTimeSection = showCheckMark || showTotalTime;
	const showLockIcon = isLocked;
	const showTimeOffIcon = (isDayFullTimeOff || isHoliday) && !isLocked;

	const [isSmallCell, setIsSmallCell] = useState(false);

	let textColor = 'default';
	if (isDayOutsideViewingMonth) {
		textColor = 'onLight';
	} else if (isLocked) {
		textColor = 'disabledOnDisabled';
	}

	const date = day.getDate();

	const handleResize = () => {
		const width = ref?.current?.getBoundingClientRect()?.width;
		if (width && width < 300) {
			setIsSmallCell(true);
		} else if (width && width >= 300) {
			setIsSmallCell(false);
		}
	};

	useEffect(() => {
		handleResize();
	}, []);

	useEffect(() => {
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	return (
		<DayCellStyle
			ref={ref}
			tabIndex={0}
			hoverable
			showCheckMark={showCheckMark}
			nonWorkingDay={isNonWorkingDay}
			locked={isLocked}
			selected={daySelected}
			onClick={() => onSelect()}
			onKeyDown={event => onKeyboardEventHandler(event, () => onSelect())}
		>
			<FlexColumn gap="s">
				<DayInfoWrapper isDayToday={isDayToday}>
					<FlexRow justifyContent="space-between">
						<FlexRow flex="0 0 auto" alignItems={'center'}>
							{isDayToday ? <TodayText>{date}</TodayText> : <CellTextBold color={textColor}>{date}</CellTextBold>}
							{date === 1 ? <CellTextBold color={textColor}>{format(day, 'MMM')}</CellTextBold> : null}
							{showLockIcon ? <Icon color={'disabledOnDisabled'} icon="lockClosed" /> : null}
							{showTimeOffIcon ? <Icon color={'disabledOnDisabled'} icon="timeOff" /> : null}
						</FlexRow>
						{isHoliday ? (
							<FlexRow flex={'1 0'} justifyContent={showTimeSection ? 'center' : 'end'}>
								<CellText color={'disabledOnDisabled'}>{holidaysOfTheDay[0].node.name}</CellText>
							</FlexRow>
						) : null}
						{showTimeSection ? (
							<FlexRow flex="0 0 auto" justifyContent={'end'}>
								<FlexRow flex={'0 0 auto'}>
									<CellText color={textColor}>
										{showTotalTime ? `${totalTimeInHoursFormatted}` : null}
										{!isSmallCell && !isNonWorkingDay ? ` / ${workingHoursFormatted}` : null}
									</CellText>
								</FlexRow>
								<FlexRow flex={'0 0 auto'}>
									{showCheckMark ? <Icon color={checkMarkColor} icon="checkCircle" /> : null}
								</FlexRow>
							</FlexRow>
						) : null}
					</FlexRow>
				</DayInfoWrapper>
				<CalendarEvents
					calendarEvents={calendarEvents}
					isEventConverted={isEventConverted}
					isLocked={isLocked}
					renderPlaceholders={isLoadingCalendarEvents && isWeekSelected}
					openModal={openModal}
					showContextMenu={showContextMenu}
				/>
			</FlexColumn>
		</DayCellStyle>
	);
};

export default DayCell;
